import { useEffect, useState } from "react";

import { insightsController } from "../../../AppController";
import { useAppSelector } from "../../../hooks/redux";

export interface MySpend {
  numerator: number;
  denominator: number | null;
  backDue: number;
  enforced: boolean;
  workflows: number;
  interactive: number;
  /** 1-indexed current day of the free trial: 1 on the first day, up to
   *  `trialDaysTotal` on the last day. Null when not in a trial.
   *  `trialDaysTotal` is the trial's full length in days. */
  trialDayNumber: number | null;
  trialDaysTotal: number | null;
  /** Grace state: the clinic's trial has ended and it is unpaid, so workflow
   *  enablement is locked until the clinic becomes paid. */
  workflowsLocked: boolean;
}

interface UseMySpendResult {
  loading: boolean;
  spend: MySpend | null;
}

const CACHE_TTL_MS = 60 * 60 * 1000;
let cache: { key: string; spend: MySpend; at: number } | null = null;
let inFlight: Promise<MySpend | null> | null = null;
let inFlightKey: string | null = null;

/** Test-only: clear the module-level spend cache between cases. */
export const __resetMySpendCacheForTests = (): void => {
  cache = null;
  inFlight = null;
  inFlightKey = null;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;
const isFresh = (key: string): boolean =>
  cache !== null && cache.key === key && Date.now() - cache.at < CACHE_TTL_MS;

const DAY_MS = 24 * 60 * 60 * 1000;

// Trial window -> {dayNumber, total}. `dayNumber` is the 1-indexed current day
// (1 on the first day, `total` on the last), clamped to the window; returns
// nulls (hide the days UI) for a missing/invalid window.
const computeTrialDays = (trial?: {
  started_at: string | null;
  expires_at: string | null;
}): { dayNumber: number | null; total: number | null } => {
  if (!trial?.started_at || !trial?.expires_at) {
    return { dayNumber: null, total: null };
  }
  const start = new Date(trial.started_at).getTime();
  const end = new Date(trial.expires_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return { dayNumber: null, total: null };
  }
  const total = Math.round((end - start) / DAY_MS);
  const dayNumberRaw = Math.floor((Date.now() - start) / DAY_MS) + 1;
  const dayNumber = Math.min(Math.max(dayNumberRaw, 1), total);
  return { dayNumber, total };
};

const fetchSpend = async (clinicId?: number): Promise<MySpend | null> => {
  const now = new Date();
  const startDt = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  ).toISOString();
  const [costResult, creditsResult] = await Promise.all([
    insightsController.getMyNeoCost({ startDt }),
    insightsController.getMyNeoCredits(clinicId ? { clinicId } : undefined),
  ]);
  if (
    !costResult.success ||
    !costResult.data ||
    !creditsResult.success ||
    !creditsResult.data
  ) {
    return null;
  }

  const cost = costResult.data;
  const credits = creditsResult.data;
  const workflows =
    cost.current_period?.workflows_usd ?? cost.workflows_usd ?? 0;
  const interactive =
    cost.current_period?.interactive_usd ?? cost.interactive_usd ?? 0;
  const numerator = round2(workflows + interactive);
  const balance = credits.balance_usd ?? 0;
  const priorSpend = Math.max(round2((credits.spent_usd ?? 0) - numerator), 0);
  const { dayNumber: trialDayNumber, total: trialDaysTotal } = computeTrialDays(
    credits.trial
  );

  return {
    numerator,
    denominator: credits.enforced
      ? round2(numerator + Math.max(balance, 0))
      : null,
    backDue: Math.max(round2(priorSpend - balance), 0),
    enforced: credits.enforced === true,
    workflows,
    interactive,
    trialDayNumber,
    trialDaysTotal,
    workflowsLocked: credits.workflows_locked === true,
  };
};

const getSpend = async (
  key: string,
  clinicId?: number
): Promise<MySpend | null> => {
  if (isFresh(key)) return cache!.spend;
  if (inFlight && inFlightKey === key) return inFlight;
  inFlightKey = key;
  inFlight = fetchSpend(clinicId)
    .then((spend) => {
      if (spend) cache = { key, spend, at: Date.now() };
      return spend;
    })
    .finally(() => {
      inFlight = null;
      inFlightKey = null;
    });
  return inFlight;
};

export const useMySpend = (options?: {
  enabled?: boolean;
}): UseMySpendResult => {
  // When disabled (e.g. the credit panel is hidden), skip the network fetch
  // entirely rather than fetching spend data nobody will see.
  const enabled = options?.enabled ?? true;
  const userKey = useAppSelector((state) => {
    const user = state.auth.user;
    return user?.userID
      ? `id:${user.userID}`
      : `email:${user?.email ?? "anonymous"}`;
  });
  const clinicId = useAppSelector((state) => state.clinic.userClinic?.id);
  // Trial is clinic-scoped, so cache per (user, clinic) — switching clinics
  // must not reuse the previous clinic's trial window.
  const cacheKey = `${userKey}|clinic:${clinicId ?? "none"}`;
  const [loading, setLoading] = useState(enabled && !isFresh(cacheKey));
  const [spend, setSpend] = useState<MySpend | null>(
    enabled && isFresh(cacheKey) ? cache!.spend : null
  );

  useEffect(() => {
    if (!enabled) {
      // Disabled: never fetch; report a settled, empty state.
      setLoading(false);
      setSpend(null);
      return;
    }
    if (isFresh(cacheKey)) {
      setSpend(cache!.spend);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    void getSpend(cacheKey, clinicId)
      .then((result) => {
        if (active) setSpend(result);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cacheKey, clinicId, enabled]);

  return { loading, spend };
};

// Displayed amounts are shown in credits, where $1 of under-the-hood spend ==
// 10 credits (product decision). All formatters below take the raw USD value
// and render credits.
export const CREDITS_PER_DOLLAR = 10;

const toCredits = (usd: number): number => usd * CREDITS_PER_DOLLAR;

// Compact credits: "15k" for thousands (whole or one decimal), otherwise a
// whole, comma-grouped count.
const formatCreditsCompact = (credits: number): string =>
  credits >= 1000
    ? `${
        Number.isInteger(credits / 1000)
          ? credits / 1000
          : (credits / 1000).toFixed(1)
      }k`
    : `${Math.round(credits).toLocaleString("en-US")}`;

export const formatSpend = (value: number): string =>
  formatCreditsCompact(toCredits(value));

export const formatSpendWhole = (value: number): string =>
  formatCreditsCompact(toCredits(value));

// Whole credit count (no "k" compaction), comma-grouped. Used for the
// per-category breakdown rows (Workflows / Chats).
export const formatSpendDollars = (value: number): string =>
  Math.round(toCredits(value)).toLocaleString("en-US");
